import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { TESTIDS } from "@/constants/testIds";
import { dealStageLabelKey } from "@/lib/dealsMeta";
import {
  ArrowRight, ArrowUpRight, Sparkles, ClipboardCheck, FileText,
  MessageSquare, Check, Circle, AlertCircle, Plus, ExternalLink,
  CalendarDays, ListChecks,
} from "lucide-react";

// Localise any stage (legacy 2.2A intake/drafting/qa/delivery or Release 2.2B enum).
const stageLabel = (t, stage) => {
  const k = dealStageLabelKey(stage);
  return k ? t(k) : (stage || "");
};

// -------- Attention derivation from real data --------
function deriveAttention(deals, drafts) {
  const items = [];
  (deals || []).forEach((d) => {
    if (d.status !== "active") return;
    if (d.stage === "intake") items.push({ id: `intake-${d.id}`, deal: d, kind: "intake" });
    else if (d.stage === "qa") items.push({ id: `qa-${d.id}`, deal: d, kind: "qa" });
    const p = d.parameters || {};
    const hasAny = Object.values(p).some((sect) =>
      sect && typeof sect === "object" && Object.values(sect).some((v) => v && String(v).trim())
    );
    if (!hasAny) items.push({ id: `empty-${d.id}`, deal: d, kind: "incomplete" });
  });
  (drafts || []).forEach((dr) => {
    if (dr.output && /\[[A-Z_][A-Z0-9_ ]{2,}\]/.test(dr.output)) {
      items.push({ id: `ph-${dr.id}`, draft: dr, kind: "placeholder" });
    }
  });
  return items.slice(0, 6);
}

function detectHasPlaceholders(draft) {
  return !!(draft?.output && /\[[A-Z_][A-Z0-9_ ]{2,}\]/.test(draft.output));
}

export default function DashboardPage() {
  const { t } = useI18n();
  const { user } = useAuth();
  const nav = useNavigate();
  const [deals, setDeals] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [company, setCompany] = useState(null);
  const [counterparties, setCounterparties] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sourceErrors, setSourceErrors] = useState({});  // {company:true, deals:true...} when a source failed

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const errs = {};
      const guard = (key, p) => p.then((v) => v).catch(() => { errs[key] = true; return null; });
      const [d, dr, c, cp, taskItems] = await Promise.all([
        guard("deals", api.listDeals()),
        guard("drafts", api.listDrafts()),
        guard("company", api.getCompany()),
        guard("counterparties", api.listCounterparties()),
        guard("tasks", api.listTasks("open")),
      ]);
      if (cancelled) return;
      setDeals(d || []); setDrafts(dr || []);
      setCompany(c); setCounterparties(cp || []);
      setTasks(taskItems || []);
      setSourceErrors(errs);
      if (Object.keys(errs).length > 0) setError(t("db_error_load"));
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [t]);

  // --- Onboarding checklist (6 real-data checks) ---
  // If a data source failed, mark the affected item's `error` flag so the UI
  // shows an "unavailable" state instead of implying it's a real "not done".
  const checklist = useMemo(() => {
    const hasCompany = !!(company && company.legal_name && company.country);
    const hasSignatory = !!(company && (company.signatories || []).some((s) => s && s.name));
    const hasBank = !!(company && (company.bank_details || "").trim().length > 0);
    return [
      { key: "1", label: t("db_check_1"), done: hasCompany,   error: !!sourceErrors.company, href: "/app/company" },
      { key: "2", label: t("db_check_2"), done: hasSignatory, error: !!sourceErrors.company, href: "/app/company" },
      { key: "3", label: t("db_check_3"), done: hasBank,      error: !!sourceErrors.company, href: "/app/company" },
      { key: "4", label: t("db_check_4"), done: counterparties.length > 0, error: !!sourceErrors.counterparties, href: "/app/counterparties" },
      { key: "5", label: t("db_check_5"), done: deals.length > 0,          error: !!sourceErrors.deals, href: "/app/deals" },
      { key: "6", label: t("db_check_6"), done: drafts.length > 0,         error: !!sourceErrors.drafts, href: "/app/drafting" },
    ];
  }, [company, counterparties, deals, drafts, sourceErrors, t]);
  const done = checklist.filter((x) => x.done).length;
  const total = checklist.length;
  const progressPct = Math.round((done / total) * 100);

  const activeDeals = useMemo(
    () => (deals || []).filter((d) => d.status === "active").slice(0, 6),
    [deals]
  );

  // --- Continue working: latest deals + drafts by updated_at ---
  const continueItems = useMemo(() => {
    const merged = [
      ...deals.filter((d) => d.status === "active").map((d) => ({
        kind: "deal", id: d.id, title: d.title, sub: d.counterparty,
        stage: d.stage, updated_at: d.updated_at,
      })),
      ...drafts.map((dr) => ({
        kind: "draft", id: dr.id, title: dr.title || dr.template_code,
        sub: dr.template_code, updated_at: dr.updated_at || dr.created_at,
        placeholders: detectHasPlaceholders(dr),
      })),
    ];
    merged.sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""));
    return merged.slice(0, 5);
  }, [deals, drafts]);

  const attention = useMemo(() => deriveAttention(deals, drafts), [deals, drafts]);
  const openTasks = useMemo(() => (tasks || []).filter((task) => task.status === "open").slice(0, 5), [tasks]);

  const completeTask = async (taskId) => {
    try {
      await api.updateTask(taskId, { status: "completed" });
      setTasks((items) => items.filter((item) => item.id !== taskId));
    } catch {
      setError(t("tasks_update_failed"));
    }
  };

  const recentDrafts = useMemo(() => {
    const list = [...(drafts || [])];
    list.sort((a, b) => (b.updated_at || b.created_at || "").localeCompare(a.updated_at || a.created_at || ""));
    return list.slice(0, 5);
  }, [drafts]);

  const dealById = useMemo(() => Object.fromEntries((deals || []).map((d) => [d.id, d])), [deals]);

  const firstName = (user?.full_name || "").trim().split(" ")[0] || "";
  const exampleDealId = (deals || [])[0]?.id;

  return (
    <div className="px-4 py-6 sm:p-6 md:p-10 xl:p-12 max-w-7xl">
      {/* Hero + primary CTA */}
      <div className="mb-10">
        <div className="kicker mb-3">— {t("db_hero_kicker")}</div>
        <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl leading-tight mb-3">
          {`${t("db_welcome")}${firstName ? ", " + firstName : ""}.`}
        </h1>
        <p className="text-[var(--td-text-2)] max-w-2xl leading-relaxed">{t("db_hero_title")}</p>

        <div className="mt-6 flex flex-col sm:flex-row sm:flex-wrap gap-3">
          <button
            data-testid="db-primary-cta"
            onClick={() => nav("/app/deals?new=1")}
            className="btn-gold inline-flex items-center justify-center gap-2 !py-3 !px-5 w-full sm:w-auto"
          >
            <Sparkles size={15} /> {t("db_primary_cta")} <ArrowRight size={15} />
          </button>
          <SecondaryBtn testid="db-secondary-doc" onClick={() => nav("/app/drafting")} icon={FileText} label={t("db_secondary_doc")} />
          <SecondaryBtn testid="db-secondary-review" onClick={() => nav("/app/review")} icon={ClipboardCheck} label={t("db_secondary_review")} />
          <SecondaryBtn testid="db-secondary-ask" onClick={() => nav("/app/expert")} icon={MessageSquare} label={t("db_secondary_ask")} />
        </div>
      </div>

      {error && (
        <div className="card-surface p-4 border-red-500/40 text-red-300 text-sm mb-8" role="alert">{error}</div>
      )}

      <section className="mb-10" aria-labelledby="db-action-center-title" data-testid="db-action-center">
        <div className="flex items-center justify-between mb-4">
          <h2 id="db-action-center-title" className="font-mono text-[11px] uppercase tracking-widest text-[var(--td-text-2)] inline-flex items-center gap-2">
            <ListChecks size={14} className="text-[var(--td-gold)]" /> {t("db_action_title")}
          </h2>
          <Link to="/app/tasks" className="font-mono text-[10px] uppercase tracking-widest text-[var(--td-text-3)] hover:text-[var(--td-gold)]">
            {t("db_action_view_all")}
          </Link>
        </div>
        <div className="card-surface">
          {loading ? (
            <div className="p-6 text-[var(--td-text-3)] font-mono text-xs">…</div>
          ) : openTasks.length === 0 ? (
            <div className="p-7 flex flex-wrap items-center justify-between gap-4">
              <span className="text-sm text-[var(--td-text-2)] italic">{t("db_action_empty")}</span>
              <Link to="/app/tasks" className="btn-ghost !py-2 !px-3 text-xs inline-flex items-center gap-2">
                <Plus size={13} /> {t("tasks_add")}
              </Link>
            </div>
          ) : (
            <ul className="divide-y divide-[var(--td-border)]">
              {openTasks.map((task) => {
                const relatedDeal = task.deal_id ? dealById[task.deal_id] : null;
                const today = new Date().toISOString().slice(0, 10);
                const overdue = task.due_date && task.due_date < today;
                return (
                  <li key={task.id} className="flex items-center gap-4 px-5 py-4" data-testid={`db-task-${task.id}`}>
                    <button type="button" onClick={() => completeTask(task.id)}
                      aria-label={t("tasks_status_completed")}
                      className="text-[var(--td-gold)] hover:text-[var(--td-gold-hover)] shrink-0">
                      <Circle size={17} />
                    </button>
                    <div className="min-w-0 flex-1">
                      <div className="font-serif text-[15px] truncate">{task.title}</div>
                      <div className="flex flex-wrap gap-3 mt-0.5 text-[10px] font-mono uppercase tracking-widest text-[var(--td-text-3)]">
                        <span className={task.priority === "high" ? "text-amber-400" : ""}>{t(`tasks_priority_${task.priority}`)}</span>
                        {task.due_date && <span className={`inline-flex items-center gap-1 ${overdue ? "text-red-400" : ""}`}><CalendarDays size={10} /> {task.due_date}{overdue ? ` · ${t("tasks_overdue")}` : ""}</span>}
                        {relatedDeal && <Link to={`/app/deals/${relatedDeal.id}`} className="text-[var(--td-gold)] hover:underline">{relatedDeal.reference || relatedDeal.title}</Link>}
                      </div>
                    </div>
                    <Link to="/app/tasks" className="text-[var(--td-text-3)] hover:text-[var(--td-gold)]"><ArrowUpRight size={14} /></Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </section>

      <div className="grid lg:grid-cols-3 gap-6 mb-10">
        {/* Onboarding checklist */}
        <div className="lg:col-span-2 card-surface p-6" data-testid="db-checklist">
          <div className="flex items-start justify-between mb-2">
            <div>
              <div className="font-mono text-[11px] uppercase tracking-widest text-[var(--td-text-2)]">{t("db_check_title")}</div>
              <div className="text-xs text-[var(--td-text-3)] mt-0.5">{t("db_check_sub")}</div>
            </div>
            <div data-testid="db-checklist-progress" className="font-mono text-[11px] text-[var(--td-gold)]">
              {t("db_check_progress").replace("{done}", done).replace("{total}", total)} · {progressPct}%
            </div>
          </div>
          <div className="h-1 bg-[var(--td-border)] rounded-full overflow-hidden mb-5">
            <div className="h-full bg-[var(--td-gold)] transition-all" style={{ width: `${progressPct}%` }} />
          </div>
          <ul className="space-y-1">
            {checklist.map((item) => (
              <li key={item.key}>
                <Link
                  to={item.href}
                  data-testid={`db-check-${item.key}`}
                  data-done={item.done ? "true" : "false"}
                  data-error={item.error ? "true" : "false"}
                  className="flex items-center gap-3 py-2 group hover:text-[var(--td-gold)] focus-visible:outline focus-visible:outline-1 focus-visible:outline-[var(--td-gold)]"
                >
                  {item.error
                    ? <AlertCircle size={15} className="text-amber-400 shrink-0" aria-label="unavailable" />
                    : item.done
                    ? <Check size={15} className="text-[var(--td-gold)] shrink-0" aria-label="done" />
                    : <Circle size={15} className="text-[var(--td-text-3)] shrink-0" aria-label="todo" />}
                  <span className={`text-sm ${item.done ? "text-[var(--td-text-3)] line-through" : "text-[var(--td-text-2)] group-hover:text-[var(--td-gold)]"}`}>
                    {item.label}
                  </span>
                  <ArrowUpRight size={12} className="ml-auto opacity-30 group-hover:opacity-100" />
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Compact membership */}
        <div className="card-surface p-6" data-testid={TESTIDS.dbMembership}>
          <div className="font-mono text-[11px] uppercase tracking-widest text-[var(--td-text-2)] mb-3">
            {t("db_section_membership")}
          </div>
          <div className="mb-3">
            <div className="font-serif text-2xl leading-tight text-[var(--td-gold)]">{t("db_plan_early")}</div>
            <div className="text-xs text-[var(--td-text-3)] mt-1">{`${t("db_renews")}: —`}</div>
          </div>
          <div className="grid grid-cols-2 gap-2 text-sm mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[var(--td-text-3)] font-mono">{t("db_usage_docs")}</div>
              <div className="font-mono text-[var(--td-text)]">{drafts.length}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[var(--td-text-3)] font-mono">{t("db_usage_deals")}</div>
              <div className="font-mono text-[var(--td-text)]">{activeDeals.length}</div>
            </div>
          </div>
          <Link to="/app/billing" data-testid="db-membership-link"
                className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-[var(--td-gold)] hover:text-[var(--td-gold-hover)]">
            {t("db_membership_link")} <ExternalLink size={11} />
          </Link>
        </div>
      </div>

      {/* Continue working + Needs attention */}
      <div className="grid lg:grid-cols-2 gap-6 mb-10">
        <div className="card-surface p-6" data-testid="db-continue">
          <div className="font-mono text-[11px] uppercase tracking-widest text-[var(--td-text-2)] mb-5">{t("db_continue_title")}</div>
          {loading ? (
            <div className="text-[var(--td-text-3)] font-mono text-xs">…</div>
          ) : continueItems.length === 0 ? (
            <div className="text-[var(--td-text-2)] py-6 text-center italic text-sm">{t("db_continue_empty")}</div>
          ) : (
            <ul className="space-y-3">
              {continueItems.map((it) => (
                <li key={`${it.kind}-${it.id}`} className="hairline-bottom pb-3">
                  <Link to={it.kind === "deal" ? `/app/deals/${it.id}` : `/app/drafting?draft=${it.id}`}
                        data-testid={`db-continue-${it.kind}-${it.id}`}
                        className="flex items-center justify-between gap-3 group hover:text-[var(--td-gold)]">
                    <div className="min-w-0">
                      <div className="font-serif text-base truncate">{it.title}</div>
                      <div className="text-xs text-[var(--td-text-3)] mt-0.5 font-mono uppercase tracking-widest truncate">
                        {it.kind === "deal" ? `${it.sub} · ${stageLabel(t, it.stage)}` : it.sub}
                        {it.kind === "draft" && it.placeholders && ` · ${t("db_recent_placeholder_status")}`}
                      </div>
                    </div>
                    <ArrowUpRight size={14} className="opacity-40 group-hover:opacity-100 shrink-0" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="card-surface p-6" data-testid="db-attention">
          <div className="font-mono text-[11px] uppercase tracking-widest text-[var(--td-text-2)] mb-5 flex items-center gap-2">
            <AlertCircle size={13} className="text-[var(--td-gold)]" /> {t("db_attention_title")}
          </div>
          {loading ? (
            <div className="text-[var(--td-text-3)] font-mono text-xs">…</div>
          ) : attention.length === 0 ? (
            <div className="text-[var(--td-text-2)] py-6 text-center italic text-sm">{t("db_attention_none")}</div>
          ) : (
            <ul className="space-y-3">
              {attention.map((a) => {
                const msg = {
                  intake: t("db_attention_intake"), qa: t("db_attention_qa"),
                  incomplete: t("db_attention_incomplete"), placeholder: t("db_attention_placeholder"),
                }[a.kind];
                const to = a.deal ? `/app/deals/${a.deal.id}` : `/app/drafting?draft=${a.draft.id}`;
                const title = a.deal ? a.deal.title : (a.draft.title || a.draft.template_code);
                return (
                  <li key={a.id} className="hairline-bottom pb-3">
                    <Link to={to} data-testid={`db-attention-${a.id}`}
                          className="flex items-center justify-between gap-3 group">
                      <div className="min-w-0">
                        <div className="font-serif text-base truncate">{title}</div>
                        <div className="text-xs text-amber-300/80 mt-0.5">{msg}</div>
                      </div>
                      <span className="font-mono text-[10px] uppercase tracking-widest text-[var(--td-gold)] shrink-0">{t("db_att_open")}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {/* Active deals working table */}
      <section className="mb-10" aria-labelledby="db-active-deals-title">
        <div className="flex items-center justify-between mb-4">
          <h2 id="db-active-deals-title" className="font-mono text-[11px] uppercase tracking-widest text-[var(--td-text-2)]">{t("db_active_title")}</h2>
          <Link to="/app/deals" className="font-mono text-[10px] uppercase tracking-widest text-[var(--td-text-3)] hover:text-[var(--td-gold)]">{t("db_view_all")}</Link>
        </div>
        {loading ? (
          <div className="card-surface p-6 text-[var(--td-text-3)] font-mono text-xs">…</div>
        ) : activeDeals.length === 0 ? (
          <div className="card-surface p-8 md:p-10" data-testid="db-active-empty">
            <div className="font-serif text-2xl mb-2">{t("db_active_empty_title")}</div>
            <div className="text-sm text-[var(--td-text-2)] max-w-md mb-5">{t("db_active_empty_sub")}</div>
            <div className="flex flex-wrap gap-2">
              <Link to="/app/deals?new=1" data-testid="db-active-create"
                    className="btn-gold inline-flex items-center gap-2"><Plus size={13} /> {t("db_active_empty_create")}</Link>
              {exampleDealId && (
                <Link to={`/app/deals/${exampleDealId}`} data-testid="db-active-example"
                      className="btn-ghost !py-2 !px-4 text-xs font-mono uppercase tracking-widest inline-flex items-center gap-2">
                  {t("db_active_empty_example")}
                </Link>
              )}
            </div>
          </div>
        ) : (
          <div className="card-surface overflow-x-auto" data-testid="db-active-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-[var(--td-border)] text-[var(--td-text-3)] font-mono text-[10px] uppercase tracking-widest">
                  <th className="px-4 py-3">{t("db_active_col_deal")}</th>
                  <th className="px-4 py-3">{t("db_active_col_cp")}</th>
                  <th className="px-4 py-3">{t("db_active_col_stage")}</th>
                  <th className="px-4 py-3">{t("db_active_col_next")}</th>
                  <th className="px-4 py-3">{t("db_active_col_attn")}</th>
                </tr>
              </thead>
              <tbody>
                {activeDeals.map((d) => {
                  const nextAction = { intake: t("db_active_next_intake"), drafting: t("db_active_next_drafting"),
                                       qa: t("db_active_next_qa"), delivery: t("db_active_next_delivery") }[d.stage] || "—";
                  const attn = attention.filter((a) => a.deal && a.deal.id === d.id).length;
                  return (
                    <tr key={d.id} className="border-b border-[var(--td-border)] last:border-b-0 hover:bg-[rgba(212,175,55,0.04)]">
                      <td className="px-4 py-3">
                        <Link to={`/app/deals/${d.id}`} className="font-serif text-[15px] hover:text-[var(--td-gold)]">
                          {d.title}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-[var(--td-text-2)]">{d.counterparty || "—"}</td>
                      <td className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-[var(--td-text-3)]">{stageLabel(t, d.stage)}</td>
                      <td className="px-4 py-3 text-[var(--td-text-2)]">{nextAction}</td>
                      <td className="px-4 py-3">
                        {attn > 0 ? (
                          <span className="inline-flex items-center gap-1 font-mono text-[10px] px-1.5 py-0.5 border border-amber-500/40 text-amber-400 uppercase tracking-widest">
                            <AlertCircle size={10} /> {attn}
                          </span>
                        ) : (<span className="text-[var(--td-text-3)]">—</span>)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Recent documents — rich context */}
      <section aria-labelledby="db-recent-title">
        <div className="flex items-center justify-between mb-4">
          <h2 id="db-recent-title" className="font-mono text-[11px] uppercase tracking-widest text-[var(--td-text-2)]">{t("db_recent_title") || "Recent documents"}</h2>
          <Link to="/app/drafting" className="font-mono text-[10px] uppercase tracking-widest text-[var(--td-text-3)] hover:text-[var(--td-gold)]">{t("db_view_all")}</Link>
        </div>
        {loading ? (
          <div className="card-surface p-6 text-[var(--td-text-3)] font-mono text-xs">…</div>
        ) : recentDrafts.length === 0 ? (
          <div className="card-surface p-8" data-testid="db-recent-empty">
            <div className="font-serif text-2xl mb-2">{t("db_recent_empty_title")}</div>
            <div className="text-sm text-[var(--td-text-2)] mb-5">{t("db_recent_empty_sub")}</div>
            <Link to="/app/drafting" className="btn-gold inline-flex items-center gap-2"><Plus size={13} /> {t("db_secondary_doc")}</Link>
          </div>
        ) : (
          <div className="card-surface overflow-x-auto" data-testid="db-recent-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-[var(--td-border)] text-[var(--td-text-3)] font-mono text-[10px] uppercase tracking-widest">
                  <th className="px-4 py-3">{t("db_recent_col_doc")}</th>
                  <th className="px-4 py-3">{t("db_recent_col_ctx")}</th>
                  <th className="px-4 py-3">{t("db_recent_col_ver")}</th>
                  <th className="px-4 py-3">{t("db_recent_col_updated")}</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {recentDrafts.map((dr) => {
                  const deal = dr.deal_id ? dealById[dr.deal_id] : null;
                  const hasPh = detectHasPlaceholders(dr);
                  const dt = dr.updated_at || dr.created_at;
                  return (
                    <tr key={dr.id} className="border-b border-[var(--td-border)] last:border-b-0 hover:bg-[rgba(212,175,55,0.04)]">
                      <td className="px-4 py-3">
                        <div className="font-serif text-[15px] truncate max-w-xs">{dr.title || dr.template_code}</div>
                        <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--td-text-3)] mt-0.5">
                          {dr.template_code}
                          {hasPh && <span className="ml-2 text-amber-400">· {t("db_recent_placeholder_status")}</span>}
                          {!hasPh && <span className="ml-2 text-emerald-400">· {t("db_recent_ready_status")}</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-[var(--td-text-2)]">{deal ? deal.title : "—"}</td>
                      <td className="px-4 py-3 font-mono text-xs">v{dr.version_number ?? "—"}</td>
                      <td className="px-4 py-3 font-mono text-xs text-[var(--td-text-3)]">{dt ? new Date(dt).toLocaleDateString() : "—"}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-3">
                          <Link to={deal ? `/app/deals/${deal.id}` : `/app/drafting?draft=${dr.id}`}
                                data-testid={`db-recent-open-${dr.id}`}
                                className="font-mono text-[10px] uppercase tracking-widest text-[var(--td-gold)] hover:text-[var(--td-gold-hover)]">
                            {hasPh ? t("db_recent_continue") : t("db_recent_open")}
                          </Link>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function SecondaryBtn({ testid, onClick, icon: Icon, label }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className="btn-ghost inline-flex items-center justify-center gap-2 !py-3 !px-4 text-sm hover:border-[var(--td-gold)] hover:text-[var(--td-gold)] transition-colors w-full sm:w-auto"
    >
      <Icon size={14} /> {label}
    </button>
  );
}
